import React, { useContext } from "react";
import { Dialog, Heading, Modal } from "react-aria-components";
import { t } from "ttag";

import { richT } from "@thelabnyc/thelabui/src/utils/richText";

import { ReactivatedContext } from "../../../context";
import { Themed } from "../../../styles/themes";
import { Clickable } from "../../Clickables";
import { Svg } from "../../Svg";
import { discountRules } from "../engine";
import { RebateBreakdown } from "./RebateBreakdown";

import styles from "./OfferDetailsModal.module.scss";

export type OfferDetailsModalProps = {
    isOpen: boolean;
    onClose: () => void;
};

export const OfferDetailsModal = ({
    isOpen,
    onClose,
}: OfferDetailsModalProps) => {
    const { STATIC_URL } = useContext(ReactivatedContext);
    const base = `${STATIC_URL}suite-builder/offer-details/`;
    const ratePct = Math.round(discountRules[0].discountRate * 100);
    return (
        <Modal
            className={styles.modal}
            isOpen={isOpen}
            onOpenChange={(open) => {
                if (!open) onClose();
            }}
            isDismissable
        >
            <Dialog className={styles.dialog}>
                {({ close }) => (
                    <>
                        <Clickable
                            className={styles.overlay}
                            onPress={close}
                            aria-hidden="true"
                            tabIndex={-1}
                        />
                        <Themed theme="offWhite" className={styles.content}>
                            <Clickable className={styles.close} onPress={close}>
                                <Svg name="x" visuallyHiddenText={t`Close`} />
                            </Clickable>
                            <Heading slot="title" className={styles.title}>
                                {richT(
                                    t`Add eligible appliance(s) from Cooking or Refrigeration, and add 1 additional appliance to unlock <strong>${ratePct}% instant rebate*</strong>.`,
                                )}
                            </Heading>
                            <div className={styles.body}>
                                <h3 className={styles.subheading}>
                                    {richT(
                                        t`<strong>Step 1</strong> Add Eligible Appliance(s)`,
                                    )}
                                </h3>
                                <div className={styles.qualifiers}>
                                    <figure className={styles.qualifier}>
                                        <img
                                            className={styles.qualifierImage}
                                            src={`${base}range.webp`}
                                            alt=""
                                        />
                                        <figcaption
                                            className={styles.qualifierCaption}
                                        >
                                            {t`1 Range`}
                                        </figcaption>
                                    </figure>
                                    <div className={styles.orDivider}>
                                        {t`or`}
                                    </div>
                                    <figure className={styles.qualifier}>
                                        <img
                                            className={styles.qualifierImage}
                                            src={`${base}cooktop-wall-oven.webp`}
                                            alt=""
                                        />
                                        <figcaption
                                            className={styles.qualifierCaption}
                                        >
                                            {t`1 Cooktop / Rangetop + 1 Wall Oven`}
                                        </figcaption>
                                    </figure>
                                    <div className={styles.orDivider}>
                                        {t`or`}
                                    </div>
                                    <figure className={styles.qualifier}>
                                        <img
                                            className={styles.qualifierImage}
                                            src={`${base}french-door.webp`}
                                            alt=""
                                        />
                                        <figcaption
                                            className={styles.qualifierCaption}
                                        >
                                            {t`1 French Door / Side-by-Side Refrigerator`}
                                        </figcaption>
                                    </figure>
                                    <div className={styles.orDivider}>
                                        {t`or`}
                                    </div>
                                    <figure className={styles.qualifier}>
                                        <img
                                            className={styles.qualifierImage}
                                            src={`${base}column-pair.webp`}
                                            alt=""
                                        />
                                        <figcaption
                                            className={styles.qualifierCaption}
                                        >
                                            {t`1 Column Refrigerator + 1 Column Freezer`}
                                        </figcaption>
                                    </figure>
                                </div>
                                <h3 className={styles.subheading}>
                                    {richT(
                                        t`<strong>Step 2</strong> Add 1 Additional Appliance`,
                                    )}
                                </h3>
                                <div className={styles.additionalImageWrapper}>
                                    <img
                                        className={styles.additionalImage}
                                        src={`${base}additional.webp`}
                                        alt={t`Examples of additional eligible appliances`}
                                    />
                                </div>
                                <RebateBreakdown />
                            </div>
                        </Themed>
                    </>
                )}
            </Dialog>
        </Modal>
    );
};
